import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import _ from 'lodash';
import clsx from 'clsx';
import { Card, Form, Button, SelectField, MenuItem, Stack } from "@curves/ui-base";
import { FilterInBodyScannerSearch } from '@icoach/inbody/overview/FilterInBodyScannerTemplate';
import { FilterUnInBodyScannerSearch } from '@icoach/inbody/overview/FilterUnInBodyScannerTemplate';
import { FilterMemberComparisonSearch } from '@icoach/inbody/overview/FilterInMemberComparisonTemplate';
import { FilterScannerResultSearch } from '@icoach/inbody/overview/FilterScannerResultTemplate';
import { initBookmark } from '@icoach/members/staticData';
import { useInBodyContext } from '@icoach/inbody/components/InBodyContext';
import useMemberFilterApi from '@apis/useMemberFilterApi';
import useMemberFilterExportApi from '@apis/useMemberFilterExportApi';
import { isEmpty, objectToQueryString, paramsToObject } from '@util/utils';
import { CLUB_INBODY_ROUTER } from '@icoach/router/routerPath';
import useExportActionDialog from '@util/hook/useExportActionDialog';
import useWindowScrollPositions from '@util/hook/useWindowScrollPositions';
import { securityMaskFilterTypeMap } from '@util/hook/usePrivacyToggle';

const numberParamKeys = ['pageIndex', 'pageSize', 'sortBy'];

// 量身快速名單 searchbar
const InbodyOverviewFilterSearchBar = React.forwardRef((props, ref) => {
    const { className: classNameProps, targetType: quickType } = props;
    const history = useHistory();
    const {
        sourceData,
        setSourceData,
        setRowsLabelType,
        setSortOption,
        bookmark,
        setBookmark,
        setBookmarkNum,
        setBookmarkSortBy,
        resource,
        setResource,
        reSetCheckedItems,
        doManualScrollTo,
    } = useInBodyContext();
    const {
        measuredOrderByOption: measuredSortOption = [],
        notYetMeasureOrderByOption: unInBodyScannerSortOption = [],
        measureResultTypeOrderByOption,
    } = resource;
    const { getMemberFilterOptionsApi, getNotYetMeasureFilterDataApi, getMeasuredFilterDataApi, getMeasuredResultApi } = useMemberFilterApi();
    const {
        postMemberFilterMeasuredApi,
        postMemberFilterMeasureNotYetApi,
        postMemberFilterMeasureResultApi,
        postMemberFilterMeasureResultExcelApi,
    } = useMemberFilterExportApi();
    const [paramsData, setParamsData] = useState({}); // 初始預設搜尋條件使用,其他狀況下無用
    const [sortStep, setSortStep] = useState(false);
    const [isMounted, setMounted] = useState(false);
    const searchPanelsRef = useRef(null);
    const preloadRef = useRef(false);
    const pendingSearchRef = useRef(null); // 重新載入頁面時，待篩選面板掛載後才執行的查詢分頁參數
    const prevFilterTypeRef = useRef(quickType);
    const { showExportConfirmDialog, showExportPolicyDialog } = useExportActionDialog();
    useWindowScrollPositions();

    const parseQueryStringToParams = () => {
        const { location } = history;
        return paramsToObject(location.search, numberParamKeys);
    };

    const getParams = (targetPage = {}) => {
        let params = { ...targetPage };
        if (searchPanelsRef && searchPanelsRef.current) {
            Object.assign(params, {
                ...searchPanelsRef.current.getResult(),
            });
        }
        return params;
    };

    const handleTypeChange = (_dom, { props }) => {
        history.push(CLUB_INBODY_ROUTER.GET_OVERVIEW_FILTER_PATH(props.value));
        preloadRef.current = false;

        setSourceData({ list: [] });
    };

    const handleSearchClick = () => {
        reSetCheckedItems();
        const params = getParams(initBookmark(bookmark?.sortBy));
        setBookmarkNum(1);
        doSearchApi(params);
        preloadRef.current = true; // 防止切換名單後無法更多與排序
    };

    const handleExcelOnClick = async () => {
        await showExportConfirmDialog(getParams(initBookmark(bookmark?.sortBy)), 
            (params) => doExcelSendEmailApi(params));
        preloadRef.current = true; // 防止切換名單後無法更多與排序
    };

    const doMemberFilterOptionsApi = async () => {
        const resp = await getMemberFilterOptionsApi();
        if (resp) {
            setResource(resp);
        }
        // 重新載入頁面才會觸發，當下的頁面切換quickType不會進來
        const { location } = history;
        const par = paramsToObject(location.search);
        let pageModel = initBookmark(); // bookmark跟不上同步,故用此參數
        if (!_.isEmpty(par)) {
            setBookmarkNum(par.pageIndex);
            setBookmarkSortBy(par.sortBy);
            pageModel.sortBy = par.sortBy;
            pageModel.pageIndex = par.pageIndex;
            setParamsData(par);
        }
        setMounted(true);
        preloadRef.current = true;
        // React 18 自動批次更新：此時篩選面板尚未 render（searchPanelsRef 為 null），改待掛載後再查詢
        if (!_.isEmpty(par)) {
            pendingSearchRef.current = pageModel;
        }
    };

    const doSearchApi = async (params) => {
        const doApi = memberFilterMap[quickType]['doApi'];
        const resp = await doApi(params);
        if (resp) {
            const { list, ...pageParams } = resp;
            setSourceData((prev) => {
                let targetList = [...list];
                if (String(params.pageIndex) !== '1') {
                    targetList = [...(Array.isArray(prev.list) ? prev.list : []), ...targetList];
                }
                return {
                    ...pageParams,
                    list: targetList,
                };
            });
            const _r = `${CLUB_INBODY_ROUTER.GET_OVERVIEW_FILTER_PATH(quickType)}${objectToQueryString(params)}`;
            history.replace(_r);
        }
        if (!_.isEmpty(paramsData)) {
            setParamsData({}); // 防止換回同類型初始參數
        }
    };

    const doExcelSendEmailApi = async (params) => {
        const doApi = memberFilterMap[quickType]['excelApi'];
        const resp = await doApi(params);

        showExportPolicyDialog(resp);
    };

    const memberFilterMap = useMemo(
        () => ({
            1: {
                description: '未量身名單',
                rowLabelType: securityMaskFilterTypeMap.ClubMemberNonMeasure,
                sortOption: unInBodyScannerSortOption,
                Component: FilterUnInBodyScannerSearch,
                excelApi: postMemberFilterMeasureNotYetApi,
                doApi: getNotYetMeasureFilterDataApi,
            },
            2: {
                description: '已量身名單',
                rowLabelType: securityMaskFilterTypeMap.ClubMemberMeasured,
                sortOption: measuredSortOption,

                Component: FilterInBodyScannerSearch,
                excelApi: postMemberFilterMeasuredApi,
                doApi: getMeasuredFilterDataApi,
            },
            3: {
                description: '量身成果名單(匯出)',
                rowLabelType: '',
                Component: FilterScannerResultSearch,
                excelApi: postMemberFilterMeasureResultExcelApi,
                doApi: null,
            },
            4: {
                description: '會員量身成果比較表',
                rowLabelType: securityMaskFilterTypeMap.ClubMemberMeasureFilter,
                sortOption: measureResultTypeOrderByOption,
                Component: FilterMemberComparisonSearch,
                excelApi: postMemberFilterMeasureResultApi,
                doApi: getMeasuredResultApi,
            },
        }),
        // eslint-disable-next-line
        [resource]
    );

    useEffect(
        () => {
            const filterTypeIsSame = String(prevFilterTypeRef.current) === String(quickType);
            const { rowLabelType = 0, sortOption = [] } = memberFilterMap[quickType];

            setRowsLabelType(rowLabelType);
            setSortOption(sortOption);

            if (!filterTypeIsSame) {
                setBookmarkSortBy(1);
                prevFilterTypeRef.current = quickType;
            }
            return () => {
                setSourceData([]);
                setRowsLabelType(0);
            };
        },
        // eslint-disable-next-line
        [quickType]
    );

    useEffect(
        () => {
            const { rowLabelType = [], sortOption = [] } = memberFilterMap[quickType];

            setRowsLabelType(rowLabelType);
            setSortOption(sortOption);

            const params = parseQueryStringToParams();

            if (!_.isEmpty(params)) {
                setParamsData(params);
                setBookmark({ pageIndex: params.pageIndex, sortBy: params.sortBy });
            } else {
                setBookmarkSortBy(1);
            }

            setSortStep('START');
        },
        // eslint-disable-next-line
        [memberFilterMap]
    );

    // 換頁
    useEffect(
        () => {
            if (preloadRef.current) {
                const params = getParams(bookmark);
                if (bookmark && String(bookmark.pageIndex) !== '1') {
                    doSearchApi(params);
                }
            }
        },
        // eslint-disable-next-line
        [bookmark.pageIndex]
    );

    // 變更排序
    useEffect(
        () => {
            if (preloadRef.current && sourceData && !isEmpty(sourceData.list)) {
                const params = getParams(bookmark);
                doSearchApi(params);
            } else if (sortStep === 'START') {
                setSortStep('COMPLETE');
            }
        },
        // eslint-disable-next-line
        [bookmark.sortBy, sortStep]
    );

    useEffect(
        () => {
            if (sortStep === 'COMPLETE') {
                let params = parseQueryStringToParams();
                if (!(!_.isEmpty(params) && _.size(sourceData) === 0)) {
                    doManualScrollTo();
                }
            }
        },
        // eslint-disable-next-line
        [sortStep]
    );

    // 功能：篩選面板掛載後（ref 已就緒）才執行重新載入頁面時的初始查詢，確保帶入網址上的篩選條件
    useEffect(
        () => {
            if (isMounted && pendingSearchRef.current) {
                const pageModel = pendingSearchRef.current;
                pendingSearchRef.current = null;
                doSearchApi(getParams(pageModel));
            }
        },
        // eslint-disable-next-line
        [isMounted]
    );

    useEffect(
        () => {
            if (_.isEmpty(resource)) {
                doMemberFilterOptionsApi();
            }
            return () => {
                setResource({});
                setBookmarkSortBy(1);
                setBookmarkNum(1);
            };
        },
        // eslint-disable-next-line
        []
    );

    const CompPanel = React.useMemo(
        () => {
            if (!isMounted) return null;
            return quickType ? memberFilterMap[quickType]['Component'] : null;
        },
        // eslint-disable-next-line
        [isMounted, quickType]
    );

    return (
        <Card className={clsx(classNameProps)}>
            <Card.CardContent className="px-4 py-3">
                <Form onSubmit={handleSearchClick}>
                    <Grid container spacing={2}>
                        <Grid item xs={2}>
                            <SelectField label="篩選名單" value={quickType} onChange={handleTypeChange} fullWidth>
                                <MenuItem value="1">未量身名單</MenuItem>
                                <MenuItem value="2">已量身名單</MenuItem>
                                <MenuItem value="3">量身成果名單(匯出)</MenuItem>
                                <MenuItem value="4">會員成果查詢</MenuItem>
                            </SelectField>
                        </Grid>
                        {isMounted && CompPanel ? <CompPanel key={quickType} ref={searchPanelsRef} paramsData={paramsData} resource={resource} /> : null}
                        <Grid xs={3} item>
                            <Stack direction={'row'} spacing={1} className="pt-4">
                                {memberFilterMap[quickType]['doApi'] && (
                                    <Button variant="contained" type="submit">
                                        搜尋
                                    </Button>
                                )}
                                {memberFilterMap[quickType]['excelApi'] && (
                                    <Button variant="contained" color="success" onClick={handleExcelOnClick}>
                                        下載Excel
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Form>
            </Card.CardContent>
        </Card>
    );
});
export default InbodyOverviewFilterSearchBar;
